import * as React from "react"
import {
  FaBars,
  FaEnvelope,
  FaHome,
  FaMapMarkedAlt,
  FaMapMarkerAlt,
  FaMobileAlt,
  FaPhoneAlt,
} from "react-icons/fa"
import QuoteForm from "./QuoteForm"
import { buildView } from "./view-model"
import "../../../styles/templates/base.css"
import "../../../styles/templates/signature.css"
import "../../../styles/templates/signature.mobile.css"
import "../../../styles/templates/port.css"

/**
 * Free Template 5 — the Mockup Maker's `signature` design, ported —
 * MTC-FCS-041.
 * ============================================================================
 * Logo-led: a full-bleed photographic hero carrying only the business name and
 * one button, a thin header of labelled links, and a claim band under the
 * fold. Same DOM and same stylesheet as
 * `source--mockup-maker/src/templates/signature`; signature.css is verbatim.
 *
 * ── THE RIBBON AND THE REVIEW BADGES ARE NOT PORTED ────────────────────────
 * Both state a review score, and there is no score on file here. A ribbon
 * reading "5.0 Rated" over a contractor's hero is a fabricated claim on their
 * actual website, not a placeholder.
 */

/**
 * ── LOCAL OR REMOTE — FCS-057(b) ───────────────────────────────────────────
 * DEFAULT_MODE is the switch this design runs in when nobody says otherwise,
 * and it is "local": the content is whatever the builder hands over, which is
 * the draft somebody is editing.
 *
 * In "remote" mode the same component draws a PUBLISHED site, and the JSON
 * fetched from Storage is the only source of anything — text, photographs,
 * contact details, colours. Nothing here reaches for a sample value in that
 * mode, because a placeholder on a real contractor's domain is a sentence
 * they never wrote on a website customers are reading. The refusing is done
 * before this point, in lib/site-content.js: a file that is missing a
 * required field never reaches a template at all.
 *
 * TEMPLATE_VERSION is what a published file is checked against, so a site
 * published against an older shape of this design is refused rather than
 * rendered wrong.
 */
export const DEFAULT_MODE = "local"
export const TEMPLATE_ID = "mm-signature"
export const TEMPLATE_VERSION = "1"

const Photo = ({ src, slot, alt }) =>
  src ? (
    <div className="mk-photo" data-slot={slot}>
      <img src={src} alt={alt || ""} />
    </div>
  ) : (
    <div className="mk-photo" data-slot={slot} aria-hidden="true" />
  )

const TemplateSignature = ({ draft, plan, mode = DEFAULT_MODE }) => {
  const v = buildView(draft, { plan, mode })
  const serviceNames = v.services.map(s => s.title).filter(Boolean)

  // The hero is a photograph, or the gallery as a stack of them.
  const slides = (v.hasGallery ? v.gallery.slice(0, 3) : [v.heroImage]).filter(Boolean)

  // The band under the hero states something about the business, so it only
  // appears when the business actually said something.
  const claim = v.slogan

  // "One Team, Start To Finish", "Straight Answers" and "Detail Oriented" were drawn for every contractor. They are claims about how
  // a business works, and nobody told us any of them. (FCS-124)

  const navItems = [
    { href: "#top", label: "Home" },
    { href: "#services", label: "Services" },
    ...(v.hasGallery ? [{ href: "#gallery", label: "Our Work" }] : []),
    ...(v.aboutText ? [{ href: "#about", label: "About" }] : []),
    { href: "#contact", label: "Contact" },
  ]

  return (
    <div className="mk mk-signature" style={v.style} data-mode="light" id="top">
      {v.hasPhone && (
        <div className="sg-callbar">
          <div className="sg-callbar-inner">
            <a href={v.phoneHref} className="sg-callbar-num">
              Call {v.phone}
            </a>
            <a href={v.phoneHref} className="sg-callbar-icon" aria-label={`Call ${v.phone}`}>
              <FaPhoneAlt />
            </a>
          </div>
        </div>
      )}

      <header className="sg-header">
        <a href="#top" className="sg-brand" aria-label={v.name}>
          <span className="sg-brand-word">
            <span className="mk-monogram sg-brand-mark">{v.initials}</span>
            <span className="sg-brand-name">{v.name}</span>
          </span>
        </a>
        <nav className="sg-header-nav">
          {serviceNames.length > 0 && (
            <a href="#services" className="sg-header-item">
              <FaHome />
              <span className="sg-header-text">
                <strong>Services</strong>
                <em>{serviceNames.join(" | ")}</em>
              </span>
            </a>
          )}
          {v.serviceArea && (
            <a href="#contact" className="sg-header-item">
              <FaMapMarkedAlt />
              <span className="sg-header-text">
                <strong>Service Area</strong>
                <em>{v.serviceArea}</em>
              </span>
            </a>
          )}
          <a href={v.hasPhone ? v.phoneHref : "#contact"} className="sg-header-item">
            <FaMobileAlt />
            <span className="sg-header-text">
              <strong>Request Quote</strong>
              <em>{v.hasPhone ? v.phone : "Get a quote"}</em>
            </span>
          </a>
        </nav>
        <span className="sg-burger">
          <FaBars />
        </span>
      </header>

      {/* ── HERO ───────────────────────────────── */}
      <section className="sg-hero">
        <div className="sg-stage" data-slides={slides.length}>
          {slides.length ? (
            slides.map((url, i) => (
              <span className="sg-slide" key={`${url}-${i}`} style={{ "--i": i }}>
                <Photo src={url} slot={i === 0 ? "hero" : `gallery${i}`} />
              </span>
            ))
          ) : (
            <span className="sg-slide">
              <Photo src="" slot="hero" />
            </span>
          )}
          <span className="sg-scrim" />
        </div>

        <div className="sg-hero-inner">
          <div className="sg-mark">
            <span className="sg-mark-word">
              <span className="sg-mark-lead">{v.lead}</span>
              {v.accent && <span className="sg-mark-accent">{v.accent}</span>}
            </span>
          </div>
          <a href="#contact" className="sg-hero-cta">
            Get a Free Estimate
          </a>
        </div>
      </section>

      {claim && (
        <div className="sg-claim">
          <span>{claim}</span>
        </div>
      )}

      {/* ── ABOUT: the contractor's own words, or nothing ─ */}
      {v.aboutText && (
        <section className="sg-trust" id="about">
          <span className="sg-blob" aria-hidden="true" />
          <div className="sg-container">
            <div className="sg-trust-head">
              <p className="sg-trust-kicker">About</p>
              <h2 className="sg-trust-title">{v.name}</h2>
            </div>
            <p className="sg-sub">{v.aboutText}</p>
          </div>
        </section>
      )}

      {/* ── SERVICES ───────────────────────────── */}
      <section className="sg-services" id="services">
        <div className="sg-container">
          <div className="sg-head">
            <h2 className="sg-title">Our Services</h2>
            <p className="sg-sub">
              We offer the following services.
              {v.hasPhone ? " Call us today at " : ""}
              {v.hasPhone && (
                <a href={v.phoneHref} className="sg-sub-link">
                  {v.phone}
                </a>
              )}
              {v.hasPhone ? " for custom inquiries." : ""}
            </p>
          </div>
          <div className="sg-grid">
            {v.services.map((service, i) => (
              <article className="sg-card" key={service.slug}>
                <span className="sg-card-photo">
                  <Photo src={service.image} slot={`service${i + 1}`} />
                </span>
                <h3 className="sg-card-title">{service.title}</h3>
                <p className="sg-card-desc">{service.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── OUR WORK ───────────────────────────── */}
      {v.hasGallery && (
        <section className="sg-work" id="gallery">
          <div className="sg-container sg-work-inner">
            <div className="sg-work-head">
              <h2 className="sg-work-title">Our Work</h2>
              <p className="sg-work-sub">
                Have a look through the gallery and see the standard of the finish for yourself.
              </p>
            </div>
            <div className="sg-work-grid">
              {v.gallery.slice(0, 6).map((url, i) => (
                <figure className="sg-work-tile mk-tile" key={`${url}-${i}`}>
                  <Photo src={url} slot={`gallery${i + 1}`} />
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── ABOUT ──────────────────────────────── */}
      {v.aboutText && (
        <section className="sg-about">
          <div className="sg-container sg-about-inner">
            <div className="sg-about-photo">
              <Photo src={v.heroImage} slot="hero" />
            </div>
            <div>
              <h2 className="sg-about-title">Transform your home with {v.name}</h2>
              <p className="sg-about-copy">{v.aboutShort}</p>
            </div>
          </div>
        </section>
      )}

      {/* ── GET IN TOUCH ───────────────────────── */}
      <section className="sg-touch" id="contact">
        <span className="sg-touch-photo">
          <Photo src={v.heroImage} slot="hero" />
        </span>
        <span className="sg-touch-scrim" />
        <div className="sg-touch-inner">
          <h2 className="sg-touch-title">Get In Touch</h2>
          <span className="sg-touch-rule" />
          {v.cityIsReal && <p className="sg-touch-copy">Serving {v.city}.</p>}
          <a href={v.hasPhone ? v.phoneHref : "#services"} className="sg-touch-btn">
            Contact Us
          </a>
          <div className="sg-touch-social">
            {v.hasEmail && (
              <a href={v.emailHref} className="sg-touch-circle" aria-label={v.email}>
                <FaEnvelope />
              </a>
            )}
            {v.hasPhone && (
              <a href={v.phoneHref} className="sg-touch-circle" aria-label={v.phone}>
                <FaPhoneAlt />
              </a>
            )}
          </div>
        </div>
      </section>


      {/* ── REQUEST A QUOTE — MTC-FCS-017 ──────── */}
      {v.wantsForm && (
        <section className="mk-quote-section" id="quote" data-premium="leadform">
          <div className="mk-quote-inner">
            <div className="mk-quote-head">
              <h2 className="sg-touch-title">Request a Quote</h2>
              <p className="mk-quote-lead">
                Tell us what you need and we will come back to you.
              </p>
            </div>
            <QuoteForm view={v} />
          </div>
        </section>
      )}
      {/* ── FOOTER ─────────────────────────────── */}
      <footer className="sg-footer">
        <div className="sg-container sg-footer-inner">
          <div>
            <p className="sg-footer-title">{v.name}</p>
            <nav className="sg-footer-links">
              {navItems.map(link => (
                <a key={link.label} href={link.href}>
                  {link.label}
                </a>
              ))}
            </nav>
          </div>
          <div className="sg-footer-brand">
            <span className="sg-footer-word">
              <span className="sg-footer-lead">{v.lead}</span>
              {v.accent && <span className="sg-footer-accent">{v.accent}</span>}
            </span>
            <div className="sg-footer-contact">
              {v.serviceArea && (
                <span>
                  <FaMapMarkerAlt /> {v.serviceArea}
                </span>
              )}
              {v.hasEmail && (
                <a href={v.emailHref}>
                  <FaEnvelope /> {v.email}
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="sg-container sg-footer-base">
          <span>
            © {v.year} {v.name}
          </span>
        </div>
      </footer>
    </div>
  )
}

export default TemplateSignature
